import { useEffect, useMemo, useRef, useState } from "react";
import { data_post } from "../functions/data-access";
import { AUTH_URL_PARAMS } from "./flow-helpers.js";

/**
 * @typedef {import("./connector-auth-constants.js").ConnectorAuthFieldType} ConnectorAuthFieldType
 *
 * @typedef {Record<string, string>} ValueMap
 *
 * @typedef {{ Key: string, Value: string }} ConnectorAuthOption
 *
 * @typedef {Record<string, { Label: string, Value: string }[]>} OptionMap
 *
 * @typedef {"initial" | "authenticated" | "connected"} Phase
 *
 * @typedef {Object} ConnectorAuthSetting
 * @property {string} Name
 * @property {string} [Label]
 * @property {string} [Placeholder]
 * @property {string} [Tooltip]
 * @property {string} [Description]
 * @property {ConnectorAuthFieldType} [FieldType]
 * @property {boolean} [Required]
 * @property {string} [Value]
 * @property {string} [ValidRegex]
 * @property {{ Label: string, Value: string }[]} [Options]
 *
 * @typedef {Object} ConnectorAuth
 * @property {ConnectorAuthSetting[]} [UserSettings]
 * @property {ConnectorAuthSetting[]} [AuthenticatedSettings]
 * @property {boolean} [HasRedirectUri]
 * @property {string} [AuthorizeUri]
 *
 * @typedef {Object} ConnectResult
 * @property {boolean} [Success]
 * @property {string} [Error]
 * @property {string} [ErrorDescription]
 * @property {string} [SessionToken]
 * @property {string} [redirectUri]
 *
 * @typedef {Object} UseConnectFlowProps
 * @property {ConnectorAuth} ConnectorAuth
 * @property {boolean} [Visible]
 * @property {boolean} [Embedded]
 * @property {boolean} [hasEstablishedCredential]
 * @property {boolean} [selectedDiffersFromEstablished]
 * @property {string} ConnectUrl
 * @property {string} ConnectWithSettingsUrl
 * @property {string} [SettingsMetadataUrl]
 * @property {string} [ValidateUrl]
 * @property {(userValuesId: string) => Promise<ValueMap>} GetUserValues
 * @property {(result?: ConnectResult) => void} [OnConnected]
 * @property {() => void} [OnAuthenticated]
 */
function useConnectFlow(/** @type {UseConnectFlowProps} */ props) {
    const [values, setValues] = useState(/** @type {ValueMap} */({}));
    const [errors, setErrors] = useState(/** @type {ValueMap} */({}));
    // Tracked apart from `errors` because a blank required field is not an error to show the user —
    // it only gates the button. See validate().
    const [hasMissingRequired, setHasMissingRequired] = useState(false);
    const [phase, setPhase] = useState(/** @type {Phase} */("initial"));
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [validationSuccess, setValidationSuccess] = useState(false);
    const [error, setError] = useState(/** @type {string | null | undefined} */(null));
    const [errorDescription, setErrorDescription] = useState(/** @type {string | null | undefined} */(null));
    const [originalConnectionValues, setOriginalConnectionValues] = useState(/** @type {ValueMap} */({}));
    const [authSettingsOptions, setAuthSettingsOptions] = useState(/** @type {OptionMap} */({}));
    const [authSettingsLoading, setAuthSettingsLoading] = useState(false);

    const hasLoadedRef = useRef(false);
    const authSettingsMetadataRef = useRef(/** @type {Record<string, ConnectorAuthOption[]> | null} */(null));
    const sessionTokenRef = useRef(/** @type {string | null} */(null));
    const popupCleanupRef = useRef(/** @type {(() => void) | null} */(null));
    // Synchronous re-entrancy guard for the submit handlers (validate/authorize/connect/save). React
    // state (isSubmitting/isValidating) only disables the button on the next render, which leaves a
    // window where a rapid second click re-runs the handler before the disable commits. Checking and
    // setting this ref before the first await rejects that re-entrant click immediately. One shared
    // ref is safe: the footer wires exactly one of these handlers at a time (mutually exclusive).
    const inFlightRef = useRef(false);

    const hasAuthenticatedSettings = !!props.ConnectorAuth?.AuthenticatedSettings && props.ConnectorAuth.AuthenticatedSettings.length > 0;
    const canConnect = !hasMissingRequired && Object.keys(errors).length === 0;

    const getConnectionValues = (/** @type {ValueMap} */ allValues) => {
        if (!props.ConnectorAuth?.UserSettings) return {};
        return props.ConnectorAuth.UserSettings.reduce((acc, setting) => {
            acc[setting.Name] = allValues[setting.Name] || "";
            return acc;
        }, /** @type {ValueMap} */({}));
    };

    const hasUnsavedChanges = useMemo(() => {
        if (phase === "initial") return false;
        if (!props.ConnectorAuth?.UserSettings) return false;
        return props.ConnectorAuth.UserSettings.some(s => {
            const current = (values[s.Name] || "").trim();
            const original = (originalConnectionValues[s.Name] || "").trim();
            return current !== original;
        });
    }, [values, originalConnectionValues, phase, props.ConnectorAuth]);

    useEffect(() => {
        if (!props.ConnectorAuth) {
            reset();
            return;
        }
        if (!hasLoadedRef.current && props.Visible) {
            hasLoadedRef.current = true;
            initFlow();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.ConnectorAuth, props.Visible]);

    const cleanAuthUrlParams = () => {
        const params = new URLSearchParams(window.location.search);
        AUTH_URL_PARAMS.forEach(p => params.delete(p));
        const search = params.toString() ? `?${params.toString()}` : "";
        window.history.replaceState({}, "", `${window.location.pathname}${search}${window.location.hash}`);
    };

    const extractOptions = (/** @type {Record<string, ConnectorAuthOption[]>} */ metadata, /** @type {ValueMap} */ currentValues) => {
        const newOptions = /** @type {OptionMap} */ ({});
        const newValues = { ...currentValues };

        for (const [key, options] of Object.entries(metadata)) {
            if (!Array.isArray(options) || options.length === 0) continue;
            newOptions[key] = options.map(opt => ({ Label: opt.Key, Value: opt.Value }));
            const currentIsValid = newValues[key] && options.some(o => o.Value === newValues[key]);
            if (!currentIsValid) newValues[key] = options[0].Value;
        }

        for (const [parentKey, options] of Object.entries(metadata)) {
            if (!Array.isArray(options) || options.length === 0) continue;
            const selectedValue = newValues[parentKey];
            const selectedOption = options.find(o => o.Value === selectedValue) ?? options[0];
            for (const [nestedKey, nestedOptions] of Object.entries(selectedOption)) {
                if (nestedKey === "Key" || nestedKey === "Value") continue;
                if (!Array.isArray(nestedOptions) || nestedOptions.length === 0) continue;
                newOptions[nestedKey] = nestedOptions.map(opt => ({ Label: opt.Key, Value: opt.Value }));
                const nestedIsValid = newValues[nestedKey] && nestedOptions.some(o => o.Value === newValues[nestedKey]);
                if (!nestedIsValid) newValues[nestedKey] = nestedOptions[0].Value;
            }
        }

        return { newOptions, newValues };
    };

    const loadAuthSettingsMetadata = async (/** @type {ValueMap} */ currentValues) => {
        if (!props.SettingsMetadataUrl) return;
        setAuthSettingsLoading(true);
        setError(null);
        setErrorDescription(null);
        try {
            const metadataSep = props.SettingsMetadataUrl.includes("?") ? "&" : "?";
            const metadataUrl = sessionTokenRef.current
                ? `${props.SettingsMetadataUrl}${metadataSep}sessionToken=${encodeURIComponent(sessionTokenRef.current)}`
                : props.SettingsMetadataUrl;
            const metadata = await data_post(metadataUrl, currentValues);
            if (metadata?.Error) {
                setError(metadata.Error);
                setErrorDescription(metadata.ErrorDescription);
                return;
            }
            authSettingsMetadataRef.current = metadata;
            const { newOptions, newValues: metadataValues } = extractOptions(metadata, currentValues);
            setAuthSettingsOptions(newOptions);
            // Merge metadata defaults under user values — user values take priority
            setValues(prev => ({ ...metadataValues, ...prev }));
        } catch {
            setError("Failed to load settings");
            setErrorDescription("Could not load settings options. You can enter values manually or try again.");
        } finally {
            setAuthSettingsLoading(false);
        }
    };

    const getSettingDefaults = (/**@type {ConnectorAuthSetting[] | undefined}*/settings) => {
        return (settings || []).reduce((a, { Name, Value }) => {
            if (Value) a[Name] = Value;
            return a;
        }, /** @type {ValueMap} */ ({}));
    };

    const initFlow = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const authSuccessParam = urlParams.get("authSuccess");
        const authErrorParam = urlParams.get("authError");
        const authErrorDescriptionParam = urlParams.get("authErrorDescription");
        const userValuesId = urlParams.get("userValuesId");
        const validateSuccessParam = urlParams.get("validateSuccess");
        const hasAuthParams = authSuccessParam || authErrorParam || validateSuccessParam;

        const sessionTokenFromUrl = urlParams.get("sessionToken");
        if (sessionTokenFromUrl) sessionTokenRef.current = sessionTokenFromUrl;

        const authSettingDefaults = getSettingDefaults(props.ConnectorAuth.AuthenticatedSettings);
        const userSettingDefaults = getSettingDefaults(props.ConnectorAuth.UserSettings);

        let newValues = /** @type {ValueMap} */ ({});

        if (hasAuthParams) {
            if (userValuesId) {
                try {
                    newValues = await props.GetUserValues(userValuesId);
                } catch {
                    // Scrub sensitive params from the address bar even on the failure path.
                    cleanAuthUrlParams();
                    setError("session_expired");
                    setErrorDescription("Authentication session expired. Please try again.");
                    setPhase("initial");
                    return;
                }
            } else {
                newValues = { ...userSettingDefaults };
            }
            newValues = { ...authSettingDefaults, ...newValues };

            setValues(newValues);
            setOriginalConnectionValues(newValues);
            validate(props.ConnectorAuth.UserSettings, newValues);
            cleanAuthUrlParams();

            if (authErrorParam) {
                setError(authErrorParam);
                setErrorDescription(authErrorDescriptionParam);
                setPhase("initial");
            } else if (validateSuccessParam === "true") {
                setValidationSuccess(true);
                setPhase("initial");
            } else if (authSuccessParam === "true") {
                props.OnAuthenticated?.();
                if (hasAuthenticatedSettings) {
                    setPhase("authenticated");
                    await loadAuthSettingsMetadata(newValues);
                } else {
                    setPhase("connected");
                    props.OnConnected?.();
                }
            }
        } else {
            newValues = { ...authSettingDefaults, ...userSettingDefaults };
            setValues(newValues);
            setOriginalConnectionValues(newValues);
            validate(props.ConnectorAuth.UserSettings, newValues);

            // The current auth already has valid credentials, so jump straight to its authenticated
            // settings. A switched-to auth (selectedDiffersFromEstablished) has none yet — it must
            // authenticate first, so leave it in "initial" to go through the validating connect/authorize path.
            if (props.hasEstablishedCredential && !props.selectedDiffersFromEstablished && hasAuthenticatedSettings) {
                setPhase("authenticated");
                await loadAuthSettingsMetadata(newValues);
            }
        }
    };
    const validate = (/** @type {ConnectorAuthSetting[] | undefined} */ settings, /** @type {ValueMap} */ inputValues) => {
        const newErrors = /** @type {ValueMap} */ ({});
        let missingRequired = false;
        if (settings) {
            for (const setting of settings) {
                const value = inputValues[setting.Name] || "";
                if (setting.Required && !value.trim()) {
                    // Deliberately no message: validate() runs on open, so a message here would put a
                    // red error indicator on every required field before the user has typed anything.
                    // The label asterisk and the disabled button already say the field is needed.
                    missingRequired = true;
                } else if (value.trim() && setting.ValidRegex?.trim()) {
                    try {
                        if (!new RegExp(setting.ValidRegex).test(value)) {
                            newErrors[setting.Name] = "Invalid format.";
                        }
                    } catch { /* invalid regex — ignore */ }
                }
            }
        }
        setErrors(newErrors);
        setHasMissingRequired(missingRequired);
        return !missingRequired && Object.keys(newErrors).length === 0;
    };

    const changeSetting = (/** @type {string} */ name, /** @type {string} */ value) => {
        const newValues = { ...values, [name]: value };
        setValues(newValues);
        validate(props.ConnectorAuth.UserSettings, newValues);
    };

    const changeAuthSetting = (/** @type {string} */ name, /** @type {string} */ value) => {
        const newValues = { ...values, [name]: value };
        const parentOptions = authSettingsMetadataRef.current?.[name];
        if (Array.isArray(parentOptions) && parentOptions.length > 0) {
            const selectedOption = parentOptions.find(o => o.Value === value) ?? parentOptions[0];
            for (const [key, nestedOptions] of Object.entries(selectedOption)) {
                if (key === "Key" || key === "Value") continue;
                if (Array.isArray(nestedOptions)) newValues[key] = "";
            }
        }
        if (authSettingsMetadataRef.current) {
            const { newOptions, newValues: resolved } = extractOptions(authSettingsMetadataRef.current, newValues);
            setAuthSettingsOptions(newOptions);
            setValues(resolved);
        } else {
            setValues(newValues);
        }
    };

    const handleConnect = async () => {
        if (inFlightRef.current) return { success: false };
        inFlightRef.current = true;
        setIsSubmitting(true);
        setError(null);
        setErrorDescription(null);
        setValidationSuccess(false);
        try {
            const connectionValues = getConnectionValues(values);
            const result = await data_post(props.ConnectUrl, connectionValues);
            if (result?.Success) {
                if (result.SessionToken) sessionTokenRef.current = result.SessionToken;
                setOriginalConnectionValues(connectionValues);
                // A successful re-connect ends ConnectComponent's edit mode, which would otherwise keep
                // the freshly loaded authenticated settings read-only and the button disabled.
                setIsEditing(false);
                if (hasAuthenticatedSettings) {
                    setPhase("authenticated");
                    await loadAuthSettingsMetadata(values);
                    return { success: true, requiresAuthSettings: true };
                } else {
                    setPhase("connected");
                    props.OnConnected?.(result);
                    return { success: true, requiresAuthSettings: false };
                }
            } else {
                setError(result?.Error);
                setErrorDescription(result?.ErrorDescription);
                return { success: false, error: result?.Error, errorDescription: result?.ErrorDescription };
            }
        } catch (err) {
            setError("Connection failed");
            setErrorDescription(err?.message);
            return { success: false };
        } finally {
            inFlightRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleSaveSettings = async () => {
        if (inFlightRef.current) return { success: false };
        inFlightRef.current = true;
        setIsSubmitting(true);
        setError(null);
        setErrorDescription(null);
        try {
            const saveSep = props.ConnectWithSettingsUrl.includes("?") ? "&" : "?";
            const saveUrl = sessionTokenRef.current
                ? `${props.ConnectWithSettingsUrl}${saveSep}sessionToken=${encodeURIComponent(sessionTokenRef.current)}`
                : props.ConnectWithSettingsUrl;
            const result = await data_post(saveUrl, values);
            if (result?.Success) {
                sessionTokenRef.current = null;
                setOriginalConnectionValues(values);
                setPhase("connected");
                props.OnConnected?.(result);
                return { success: true };
            } else {
                setError(result?.Error);
                setErrorDescription(result?.ErrorDescription);
                return { success: false };
            }
        } catch (err) {
            setError("Connection failed");
            setErrorDescription(err?.message);
            return { success: false };
        } finally {
            inFlightRef.current = false;
            setIsSubmitting(false);
        }
    };

    // Apply the result of an OAuth round-trip that came back via postMessage (popup/embedded flow)
    // instead of through the address bar. Mirrors the authSuccess branch of initFlow.
    /**
     * @param {{ authError?: string, authSuccess?: string, authErrorDescription?: string,
     *           userValuesId?: string, sessionToken?: string }} data
     */
    const applyAuthResult = async (data) => {
        if (data.authError || data.authSuccess === "false") {
            setError(data.authError || "authentication_failed");
            setErrorDescription(data.authErrorDescription);
            setPhase("initial");
            return;
        }
        let newValues = /** @type {ValueMap} */ ({});
        if (data.userValuesId) {
            try {
                newValues = await props.GetUserValues(data.userValuesId);
            } catch {
                setError("session_expired");
                setErrorDescription("Authentication session expired. Please try again.");
                setPhase("initial");
                return;
            }
        }
        const authSettingDefaults = getSettingDefaults(props.ConnectorAuth.AuthenticatedSettings);
        newValues = { ...authSettingDefaults, ...newValues };
        setValues(newValues);
        setOriginalConnectionValues(newValues);
        validate(props.ConnectorAuth.UserSettings, newValues);
        if (data.sessionToken) sessionTokenRef.current = data.sessionToken;
        props.OnAuthenticated?.();
        if (hasAuthenticatedSettings) {
            setPhase("authenticated");
            await loadAuthSettingsMetadata(newValues);
        } else {
            setPhase("connected");
            props.OnConnected?.();
        }
    };

    // Embedded (widget in a third-party iframe): we can't navigate the host frame to the provider,
    // so OAuth runs in a popup and we resume when its return page postMessages back to us. The popup
    // must be opened synchronously inside the click (see handleAuthorize) or the browser blocks it;
    // here we just attach the listeners to the already-open window.
    const wireOAuthPopup = (/** @type {Window} */ popup) => {
        const onMessage = async (/** @type {MessageEvent} */ e) => {
            if (e.origin !== window.location.origin) return;
            if (e.data?.type !== "popdock-oauth-complete") return;
            cleanup();
            await applyAuthResult(e.data);
        };
        const poll = setInterval(() => {
            // User closed the popup without finishing — stop the spinner so they can retry.
            if (popup.closed) { cleanup(); setIsSubmitting(false); }
        }, 500);
        const cleanup = () => {
            clearInterval(poll);
            window.removeEventListener("message", onMessage);
            popupCleanupRef.current = null;
        };
        popupCleanupRef.current = cleanup;
        window.addEventListener("message", onMessage);
    };

    const handleAuthorize = async () => {
        if (inFlightRef.current) return { success: false };
        inFlightRef.current = true;
        setIsSubmitting(true);
        setError(null);
        setErrorDescription(null);
        // Clear inFlightRef everywhere setIsSubmitting(false) is cleared — and nowhere else. The one
        // exception is the full-page redirect success branch below, which keeps both set because the
        // page is navigating away.
        // Open the popup NOW, synchronously, while we still have the click's user activation — a
        // window.open after the awaited authorize call would be treated as unsolicited and blocked.
        let /** @type {Window | null} */popup = null;
        if (props.Embedded) {
            popup = window.open("", "popdock-oauth", "width=600,height=720");
            if (!popup) {
                setError("popup_blocked");
                setErrorDescription("Your browser blocked the sign-in window. Please allow pop-ups for this site and try again.");
                inFlightRef.current = false;
                setIsSubmitting(false);
                return { success: false };
            }
        }
        try {
            const result = await data_post(props.ConnectorAuth.AuthorizeUri, getConnectionValues(values));
            if (result?.Success && result?.redirectUri) {
                if (props.Embedded) {
                    popup.location.href = result.redirectUri;
                    wireOAuthPopup(popup);
                    inFlightRef.current = false;
                    setIsSubmitting(false);
                    return { success: true, popup: true };
                }
                window.location.href = result.redirectUri;
                // Page navigates away — intentionally no setIsSubmitting(false) and no inFlightRef reset
            } else {
                if (popup) popup.close();
                setError(result?.Error);
                setErrorDescription(result?.ErrorDescription);
                inFlightRef.current = false;
                setIsSubmitting(false);
                return { success: false };
            }
        } catch (err) {
            if (popup) popup.close();
            setError("Authorization failed");
            setErrorDescription(err?.message);
            inFlightRef.current = false;
            setIsSubmitting(false);
            return { success: false };
        }
    };

    const handleValidate = async () => {
        if (!props.ValidateUrl) return;
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        // On the OAuth redirect path the page navigates away, so keep the lock (and the spinner) held
        // rather than letting the finally re-enable the button mid-navigation.
        let redirecting = false;
        setIsValidating(true);
        setError(null);
        setErrorDescription(null);
        setValidationSuccess(false);
        try {
            if (props.ConnectorAuth.HasRedirectUri) {
                const sep = props.ConnectorAuth.AuthorizeUri.includes("?") ? "&" : "?";
                const uri = `${props.ConnectorAuth.AuthorizeUri}${sep}validateOnly=true`;
                const result = await data_post(uri, getConnectionValues(values));
                if (result?.Success && result.redirectUri) {
                    redirecting = true;
                    window.location.href = result.redirectUri;
                } else {
                    setError(result?.Error);
                    setErrorDescription(result?.ErrorDescription);
                }
            } else {
                const result = await data_post(props.ValidateUrl, getConnectionValues(values));
                if (result?.Error || result?.ErrorDescription) {
                    setError(result?.Error);
                    setErrorDescription(result?.ErrorDescription);
                } else {
                    setValidationSuccess(true);
                }
            }
        } catch (err) {
            setError("Validation failed");
            setErrorDescription(err?.message);
        } finally {
            if (!redirecting) {
                inFlightRef.current = false;
                setIsValidating(false);
            }
        }
    };

    const reloadAuthSettingsMetadata = () => {
        loadAuthSettingsMetadata(values);
    };

    const editConnection = () => setIsEditing(true);

    const cancelEdit = () => {
        setValues({ ...values, ...originalConnectionValues });
        setIsEditing(false);
    };

    const reset = () => {
        popupCleanupRef.current?.();
        hasLoadedRef.current = false;
        authSettingsMetadataRef.current = null;
        sessionTokenRef.current = null;
        inFlightRef.current = false;
        setValues({});
        setErrors({});
        setHasMissingRequired(false);
        setPhase("initial");
        setIsEditing(false);
        setIsSubmitting(false);
        setIsValidating(false);
        setValidationSuccess(false);
        setError(null);
        setErrorDescription(null);
        setOriginalConnectionValues({});
        setAuthSettingsOptions({});
        setAuthSettingsLoading(false);
    };

    return {
        values,
        errors,
        phase,
        isEditing,
        isSubmitting,
        isValidating,
        validationSuccess,
        hasUnsavedChanges,
        error,
        errorDescription,
        authSettingsOptions,
        authSettingsLoading,
        hasAuthenticatedSettings,
        canConnect,
        // The staged-credentials session token, for callers that need to reach the source system
        // before a connector row exists (the onboarding list picker). A getter, not state: the ref
        // is written outside React's render cycle and consumers read it inside an effect, so
        // promoting it to state would only add a render without changing what they see.
        getSessionToken: () => sessionTokenRef.current,
        changeSetting,
        changeAuthSetting,
        handleConnect,
        handleSaveSettings,
        handleAuthorize,
        handleValidate,
        reloadAuthSettingsMetadata,
        editConnection,
        cancelEdit,
        reset,
        setValues,
        setError,
        setErrorDescription,
    };
}

export { useConnectFlow };
